import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { Gradientsegmentbar } from "@shared/components/gradientsegmentbar/gradientsegmentbar";
import { InboxNotificationCard } from "./components/inbox-notification-card/inbox-notification-card";
import { ImportantDatesCard } from "./components/important-dates-card/important-dates-card";
import { Hearing } from "@core/models/hearing";
import { UserServiceRequestSummary } from '@core/services/service-request';
import { Notification } from "@core/models/notification";
import { Appointment } from '@core/models/appointment';
import { LinkedCase } from '@core/models/linkedCase';
import { AppointmentCard } from "./components/appointment-card/appointment-card";
import { ServiceRequestCard } from "./components/service-request-card/service-request-card";
import { RouterLink } from '@angular/router';
import { UserService } from '@core/services/user';
import { Announcement } from "@shared/components/announcement/announcement";
import { AnnouncementService, AnnouncementResponse } from '@core/services/announcement';
import { ServiceRequestService } from '@core/services/service-request';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const TERMINAL_SERVICE_REQUEST_STATUSES = new Set(['completed', 'closed']);



//TODO: implement actual Dashboard
export interface DashboardData {
  notifications: Notification[];
  serviceRequests: UserServiceRequestSummary[];
  linkedCases: LinkedCase[];
  hearings: Hearing[];
  appointments: Appointment[];
}


@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, MdbRippleModule, Gradientsegmentbar, ServiceRequestCard, RouterLink, Announcement],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})

//TODO: Announcement Banner

export class Dashboard {
  dashboardData = signal<DashboardData | null>(null);
  announcementData = signal<AnnouncementResponse | null>(null);
  private userService = inject(UserService);
  private announcementService = inject(AnnouncementService);
  userServiceRequests = signal<UserServiceRequestSummary[] | null>(null);
  private destroyRef = inject(DestroyRef);
  private serviceRequestService = inject(ServiceRequestService);
  isServiceRequestLoading = signal(false);
  // Alert visibility
  showWarningAlert = signal(false);
  showInfoAlert = signal(false);
  // Empty state toggles
  showEmptyStates = signal(false);

  //If we have a fresh account - evenly space/ resize the cards to a uniform size.
  //This way users will see a clean even card layout on first arrival
  //once a user has information for their account - we can allow the cards to adjust based
  //on their content.
  isEmpty = computed(() => {
    const data = this.dashboardData();
    return !data?.notifications?.length &&
      !data?.serviceRequests?.length &&
      !data?.hearings?.length &&
      !data?.appointments?.length;
  });
  ngOnInit(): void {
    this.loadDashboardData();
  }

  private loadDashboardData(): void {
    const userInfo = this.userService.getUserInfoFromStorage();
    const userId = userInfo?.userId ?? 0;

    setTimeout(() => {}, 500);

    this.announcementService.getAllAnnouncements().subscribe({
      next: (data) => {
        this.announcementData.set(data ?? null);
      },
      error: (err) => {
        console.error('Dashboard - Announcements error:', err);
      }
    });

    this.loadServiceRequests();

  }
  // TODO: Replace with actual API call using auserId
  // this.dashboardService.getDashboardData(userId).subscribe({
  //   next: (data) => {
  //     this.dashboardData.set(data);
  //     this.loading.set(false);
  //   },
  //   error: (err) => {
  //     this.error.set('Failed to load dashboard data');
  //     this.loading.set(false);
  //     console.error('Dashboard error:', err);
  //   }
  // });

  onInboxClick(): void {
    // Navigate to inbox page or handle inbox action
    console.log('Inbox clicked');
  }

  onNotificationClick(notification: Notification): void {
    // Handle notification click (e.g., navigate to related case/appointment)
    console.log('Notification clicked:', notification);
  }

  //#region Service Requests
  //Get Service Request List
  private loadServiceRequests(): void {
    this.isServiceRequestLoading.set(true);
    this.serviceRequestService.getUserServiceRequests()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (requests) => {
          const sorted = [...requests]
            .filter((request) =>
              !TERMINAL_SERVICE_REQUEST_STATUSES.has(request.status.trim().toLowerCase())
            )
            .sort((a, b) => {
              const aDate = new Date(a.modifiedDate ?? a.submittedDate).getTime();
              const bDate = new Date(b.modifiedDate ?? b.submittedDate).getTime();
              return bDate - aDate;
            })
            .slice(0, 3);

          this.userServiceRequests.set(sorted);
          this.isServiceRequestLoading.set(false);
        },
        error: (err) => {
          this.isServiceRequestLoading.set(false);
          console.error('Dashboard - Service Requests error:', err);
        }
      });
  }
  //#endregion
}



//#region Mock Data
/*
const MOCK_DASHBOARD_DATA: DashboardData = {
  notifications: [
    {
      icon: 'schedule',
      text: 'Reminder: Upcoming hearing tomorrow 11:00am',
      time: '2h ago'
    },
    {
      icon: 'schedule',
      text: 'Reminder: Family Law Intake',
      time: '1d ago'
    },
    {
      icon: 'mail_outline',
      text: 'Workshop Confirmation: "Small Claims Basics"',
      time: '2d ago'
    }
  ],
  serviceRequests: [
    {
      createdDate: '2026-02-28',
      serviceRequestNo: 'SR-0012',
      serviceRequestType: 'Document Review',
      subject: 'Petition Review',
      description: 'Need assistance reviewing my FL-100 petition form for completeness and accuracy before filing.',
      modifiedDate: '03-01-2026',
      status: 'In Progress'
    },
    {
      createdDate: '2026-03-01',
      serviceRequestNo: 'SR-0016',
      serviceRequestType: 'Document Review',
      subject: 'Tenant Answer Review',
      description: 'Request review of my answer to unlawful detainer complaint before the filing deadline.',
      modifiedDate: '03-02-2026',
      status: 'Pending Review'
    },
    {
      createdDate: '2026-03-01',
      serviceRequestNo: 'SR-0017',
      serviceRequestType: 'Document Review',
      subject: 'Document Review',
      description: 'Request review of my answer to unlawful detainer complaint before the filing deadline.',
      modifiedDate: '03-03-2026',
      status: 'Pending Review'
    }
  ],
  linkedCases: [{
    url: "testurl",
    caseId: "SB-C-019562"
  },
  {
    url: "testurl",
    caseId: "SB-C-99962"
  }],
  hearings: [{
    date: 'MONDAY - MARCH 3, 2026',
    time: '10:00 AM',
    location: 'SB-204',
    title: 'Family Law Intake - 30m'
  },
  {
    date: 'MONDAY - MARCH 3, 2026',
    time: '12:00 PM',
    location: 'DEPT 3',
    title: 'Case #23-FL-1023 - Custody Hearing'
  },
  {
    date: 'THURSDAY - MARCH 6, 2026',
    time: '10:00 AM',
    location: 'SB-204',
    title: 'Family Law Intake - 30m'
  }],
  appointments: [{
    date: 'MONDAY - MARCH 3, 2026',
    time: '2:00 PM',
    location: 'Online',
    videoLink: 'https://sanbernardino.courts.ca.gov/',
    title: 'Workshop - Guide to the Courts',
    attendanceType: 'video',
    icon: 'test',
    type: 'hearing'
  }]
};

const EMPTY_DASHBOARD_DATA: DashboardData = {
  notifications: [],
  serviceRequests: [],
  linkedCases: [],
  hearings: [],
  appointments: []
};
*/
//#endregion
